import React, { Component } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Paper, Typography, Divider } from '@material-ui/core';
import PrevNext from '../../../prevAndNext/PrevNext';

import User from '../../../../models/User';
import Cart from '../../../../models/Cart';
import CartItem from '../../../../models/CartItem';
import CartService from '../../../../models/CartService';
import { receiveCart, updateWholeCart } from '../../../../store/actions/cartAction';
import { receiveError, receiveSuccess } from '../../../../store/actions/notificationAction';
import cartRemote from '../../../../api/cartRemote';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { getAdditionalServiceName } from '../../../../utils/additionalServices';
import commonStyles from '../../../common/common.module.css';
import AddBoxRounded from '@material-ui/icons/AddBoxRounded';
import { Tooltip } from '@material-ui/core';
import AdditionalServicesModalContainer from '../../../cart/additionalServices/additionalServicesModal/AdditionalServicesModalContainer';
import styles from './goCart.module.css';
import back from '../../../../assets/back.png';


interface GoCartState {
  loading: boolean;
  addServicesModalOpen: boolean;
  selectedAwb: any;
  success: boolean;
}

interface GoCartProps extends RouteComponentProps {
  user: User;
  cart: Cart;
  getCartItems: () => void;
  updateCart: (cart: Cart) => void;
  showError: (error: any) => void;
  showSuccess: (msg: string) => void;
}

class GoCartContainer extends Component<GoCartProps, GoCartState> {
  constructor(props: GoCartProps) {
    super(props);
    this.state = {
      loading: false,
      addServicesModalOpen: false,
      selectedAwb: null,
      success: false
    };
  }

  componentDidMount() {
    this.props.getCartItems();
  }

  handleCloseServicesModal = () => {
    this.setState({ addServicesModalOpen: false, selectedAwb: null });
    this.props.getCartItems();
  }

  handleOpenServicesModal = (item: CartItem) => {
    this.setState({ addServicesModalOpen: true, selectedAwb: item });
  }

  handleProceed = async () => {
    this.setState({ loading: true });
    try {
      const editOrderNumber = this.props.location.state?.editOrderNumber || sessionStorage.getItem('editGoOrderNumber');
      let resp;
      if (editOrderNumber) {
        resp = await cartRemote.cartGenGo(editOrderNumber);
      } else {
        resp = await cartRemote.cartGenGo();
      }
      if (resp.result) {
        this.props.updateCart(resp.result);
        if (editOrderNumber) {
            sessionStorage.removeItem('editGoOrderNumber');
            this.props.showSuccess('GO Order updated successfully!');
        } else {
            this.props.showSuccess('GO Order generated successfully!');
        }
        this.setState({ loading: false, success: true });
        setTimeout(() => {
          this.props.history.push('/my-go-orders');
        }, 1500);
      } else if (resp.error) {
        this.props.showError(resp.error);
        this.setState({ loading: false });
      }
    } catch (e) {
      console.error('Payment failed:', e);
      this.setState({ loading: false });
    }
  }

  handleBack = () => {
    this.props.history.push('/create-go-order');
  }

  getDisplayPiecesWeight = (item: any): { pieces: number; weight: number } => {
    if (Array.isArray(item.parts) && item.parts.length > 0) {
      return item.parts.reduce((acc: { pieces: number; weight: number }, part: any) => ({
        pieces: acc.pieces + (Number(part.pcs ?? part.pieces) || 0),
        weight: acc.weight + (Number(part.wgt ?? part.weight) || 0)
      }), { pieces: 0, weight: 0 });
    }

    return {
      pieces: Number(item.pieces) || 0,
      weight: Number(item.weight) || 0
    };
  }

  render() {
    if (this.state.success) {
      return (
        <div className={ `${commonStyles.wrapper} ${styles.pageWrap}` }>
          <div className={ styles.successState }>
            <CheckCircleOutlineIcon className={ styles.successIcon } />
            <Typography variant="h4" className={ styles.successTitle }>Order Confirmed!</Typography>
            <Typography className={ styles.successText }>Your GO Order has been successfully generated. Redirecting...</Typography>
          </div>
        </div>
      );
    }

    const { cart } = this.props;
    const items = (cart && cart.items) ? cart.items : [];
    const hasItems = items.length > 0;
    const totals = items.reduce((acc: { pieces: number; weight: number }, item: any) => {
      const display = this.getDisplayPiecesWeight(item);
      return {
        pieces: acc.pieces + display.pieces,
        weight: acc.weight + display.weight
      };
    }, { pieces: 0, weight: 0 });

    return (
      <div className={ `${commonStyles.wrapper} ${styles.pageWrap}` }>
        <div className={ styles.contentCard }>
          <h3 className={ `${commonStyles.header} ${styles.headerText}` }>Cart & Charges</h3>
          <p className={ styles.subtitle }>
            Review the accepted Airway Bills and the generated charges below.
          </p>

          { hasItems ? (
            <div className={ styles.mainGrid }>
              
              {/* Left side: AWB List */}
              <div className={ styles.shipmentsPane }>
                <Paper className={ styles.shipmentCard } elevation={0}>
                <Typography variant="subtitle1" className={ styles.sectionTitle }>Confirmed Shipments</Typography>
                { items.map((item: CartItem, idx: number) => {
                  const display = this.getDisplayPiecesWeight(item);

                  return (
                  <div key={idx} className={ styles.shipmentBlock }>  
                    <div className={ styles.shipmentHead }>
                      <div>
                        <Typography variant="subtitle1" className={ styles.awbNumber }>{item.code}-{item.number}</Typography>
                        <Typography variant="body2" className={ styles.awbMeta }>{display.pieces} Pieces • {display.weight} kg</Typography>
                      </div>
                      <div className={ styles.amountBlock }>
                        <Typography variant="subtitle1" className={ styles.amountValue }>${((item.remainDue !== undefined && item.remainDue !== null) ? item.remainDue : (item.totalDue || 0)).toFixed(2)}</Typography>
                        <Typography variant="caption" className={ styles.amountLabel }>Due</Typography>
                      </div>
                    </div>
                    
                    {item.services && item.services.length > 0 && (
                      <div className={ styles.servicesBox }>
                        <div className={ styles.servicesHeader }>
                          <Typography variant="subtitle2" className={ styles.servicesTitle }>Included Charges</Typography>
                          <Tooltip title="Add additional services">
                            <AddBoxRounded className={ styles.addServiceIcon } color='primary' onClick={ () => this.handleOpenServicesModal(item) } />
                          </Tooltip>
                        </div>
                        {item.services.map((service: CartService, sIdx: number) => {
                          const serviceName = service.type === 'storage_fee' && !item.storage && item.part ? `${getAdditionalServiceName(service.type)} (Not applied On Part)` : service.type === 'storage_fee' && item.part ? `${getAdditionalServiceName(service.type)} (Applied On Part)` : getAdditionalServiceName(service.type);
                          const appendText = (service.type === 'other' || service.type === 'gha_adjust') && service.params && service.params.reason ? ` - ${service.params.reason}` : '';
                          
                          return (
                            <div key={sIdx} className={ styles.serviceRow }>
                              <Typography variant="body2" className={ styles.serviceName }>{serviceName}{appendText}</Typography>
                              <Typography variant="body2" className={ styles.serviceAmount }>${(typeof service.remain === 'number' ? service.remain : (service.total || 0)).toFixed(2)}</Typography>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    </div>
                  )})}
                  </Paper>
              </div>

              {/* Right side: Order Summary */}
              <div className={ styles.summaryPane }>
                <Paper className={ styles.summaryCard } elevation={0}>
                  <Typography variant='subtitle1' className={ styles.sectionTitle }>Order Summary</Typography>
                  
                  <div className={ styles.summaryRow }>
                    <Typography className={ styles.summaryLabel }>Total Shipments</Typography>
                    <Typography className={ styles.summaryValue }>{items.length}</Typography>
                  </div>
                  
                  <div className={ styles.summaryRow }>
                    <Typography className={ styles.summaryLabel }>Total Pieces</Typography>
                    <Typography className={ styles.summaryValue }>{totals.pieces}</Typography>
                  </div>

                  <div className={ `${styles.summaryRow} ${styles.weightRow}` }>
                    <Typography className={ styles.summaryLabel }>Total Weight</Typography>
                    <Typography className={ styles.summaryValue }>{totals.weight} kg</Typography>
                  </div>

                  <Divider className={ styles.summaryDivider } />

                  <div className={ styles.totalRow }>
                    <Typography variant="subtitle1" className={ styles.totalLabel }>Total Charges</Typography>
                    <Typography variant="subtitle1" className={ styles.totalValue }>${((cart.remainDue !== undefined && cart.remainDue !== null) ? cart.remainDue : (cart.totalDue || 0)).toFixed(2)}</Typography>
                  </div>

                  <Button 
                    color="primary"
                    variant="contained"
                    fullWidth
                    className={ styles.primaryAction }
                    disabled={this.state.loading}
                    onClick={this.handleProceed}
                  >
                    { this.state.loading ? 'Processing...' : ((this.props.location.state?.editOrderNumber || sessionStorage.getItem('editGoOrderNumber')) ? 'Update GO Order' : 'Create GO Order') }
                  </Button>
                </Paper>
              </div>
              
            </div>
          ) : (
            <div className={ styles.emptyState }>
              <span className={ styles.emptyText }>Your cart is empty.</span>
            </div>
          )}

          <PrevNext prevButton={ { active: true, href: '/create-go-order', animate: true } } />

          { this.state.addServicesModalOpen && this.state.selectedAwb &&
            <AdditionalServicesModalContainer
              modalOpen={ this.state.addServicesModalOpen }
              handleClose={ this.handleCloseServicesModal }
              selectedAwbCode={ this.state.selectedAwb.code }
              selectedAwbNumber={ this.state.selectedAwb.number }
              selectedAwbVer={ this.state.selectedAwb.version }
            />
          }
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  cart: state.cart
});

const mapDispatchToProps = (dispatch: Function) => ({
  getCartItems: () => dispatch(receiveCart(true)),
  updateCart: (cart: Cart) => dispatch(updateWholeCart(cart)),
  showError: (error: any) => dispatch(receiveError(error)),
  showSuccess: (msg: string) => dispatch(receiveSuccess(msg))
});

export default connect(mapStateToProps, mapDispatchToProps)(GoCartContainer);
